import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { fileToCompressedDataUrl } from "../lib/image";

export default function DriverVerify() {
  const { user, refresh } = useAuth();
  const status = user?.driver_verification_status;
  const [vehicle, setVehicle] = useState(user?.driver_vehicle_type || "bike");
  const [idNum, setIdNum] = useState("");
  const [idImg, setIdImg] = useState(null);
  const [licenseImg, setLicenseImg] = useState(null);
  const [bookletImg, setBookletImg] = useState(null);
  const [contract, setContract] = useState(false);
  const [busy, setBusy] = useState(false);

  const pick = (setter) => async (e) => { const f = e.target.files?.[0]; if (!f) return; try { setter(await fileToCompressedDataUrl(f, 1080, 0.6)); } catch {} };

  const submit = async () => {
    if (!idNum.trim()) return toast.error("رقم الهوية مطلوب");
    if (!idImg || !licenseImg || !bookletImg) return toast.error("جميع المستندات مطلوبة");
    if (!contract) return toast.error("يجب الموافقة على العقد");
    setBusy(true);
    try {
      await api("/driver/apply", { method: "POST", body: JSON.stringify({
        vehicle_type: vehicle, national_id_number: idNum.trim(), national_id_base64: idImg,
        license_base64: licenseImg, vehicle_booklet_base64: bookletImg, contract_accepted: true,
      }) });
      await refresh();
      toast.success("تم إرسال طلب التوثيق — بانتظار مراجعة الإدارة");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (status === "approved") {
    return <div className="max-w-lg mx-auto animate-fade-in-up"><PageHeader title="توثيق حساب السائق" /><div className="p-6 bg-[#D9F8E8] rounded-2xl text-center"><ShieldCheck size={48} className="text-[#00D26A] mx-auto" /><p className="font-black text-[#161629] mt-3">حسابك موثّق ✅</p></div></div>;
  }

  return (
    <div className="animate-fade-in-up max-w-lg mx-auto">
      <PageHeader title="توثيق حساب السائق" />
      {status === "pending" && <div className="p-4 rounded-2xl bg-[#FFF7E0] border border-[#FFB627] text-[#B07700] text-sm font-bold mb-4">طلبك قيد المراجعة من الإدارة. يمكنك تحديث المستندات وإعادة الإرسال.</div>}
      {status === "rejected" && <div className="p-4 rounded-2xl bg-[#FFE5EA] text-[#FF3D5A] text-sm font-bold mb-4">تم رفض الطلب. {user?.driver_verification_note || ""} — يمكنك إعادة الإرسال.</div>}

      <p className="text-sm font-bold mb-1.5">نوع المركبة</p>
      <div className="flex gap-2">
        {[["bike", "دراجة"], ["car", "سيارة"]].map(([k, l]) => <button key={k} onClick={() => setVehicle(k)} className={`flex-1 py-3 rounded-xl font-black ${vehicle === k ? "bg-[#FF3D5A] text-white" : "bg-white border border-[#E8EAF2]"}`}>{l}</button>)}
      </div>
      <p className="text-sm font-bold mt-4 mb-1.5">رقم الهوية الوطنية</p>
      <input data-testid="dv-idnum" value={idNum} onChange={(e) => setIdNum(e.target.value)} className="w-full bg-white rounded-xl px-4 py-3 border border-[#E8EAF2] outline-none focus:ring-2 focus:ring-[#FF3D5A]" />

      <DocUpload label="صورة الهوية" img={idImg} onPick={pick(setIdImg)} t="dv-id" />
      <DocUpload label="صورة رخصة القيادة" img={licenseImg} onPick={pick(setLicenseImg)} t="dv-license" />
      <DocUpload label="صورة كتيب المركبة" img={bookletImg} onPick={pick(setBookletImg)} t="dv-booklet" />

      <label className="flex items-center gap-2 mt-4 cursor-pointer">
        <input data-testid="dv-contract" type="checkbox" checked={contract} onChange={(e) => setContract(e.target.checked)} className="w-5 h-5 accent-[#FF3D5A]" />
        <span className="text-sm font-bold text-[#161629]">أوافق على العقد والشروط والأحكام</span>
      </label>
      <button data-testid="dv-submit" disabled={busy} onClick={submit} className="w-full h-13 py-4 rounded-2xl bg-[#FF3D5A] text-white font-black mt-5 disabled:opacity-60">{busy ? "..." : "إرسال طلب التوثيق"}</button>
    </div>
  );
}
function DocUpload({ label, img, onPick, t }) {
  return (
    <>
      <p className="text-sm font-bold mt-4 mb-1.5">{label}</p>
      <label data-testid={t} className="flex flex-col items-center justify-center h-32 rounded-2xl border-2 border-dashed border-[#FF3D5A] bg-[#FFE5EA] cursor-pointer overflow-hidden">
        {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <><Upload size={24} className="text-[#FF3D5A]" /><span className="text-xs text-[#FF3D5A] font-bold mt-1">ارفع الصورة</span></>}
        <input type="file" accept="image/*" className="hidden" onChange={onPick} />
      </label>
    </>
  );
}
